/**
 * Feature Gating Utility
 * Enforces free plan limits. The numbers live in FREE_LIMITS below; this
 * comment used to duplicate them and had drifted on five of the seven.
 *
 * While subscription is loading, actions are ALLOWED (optimistic).
 * Once loaded, real limits apply.
 */

import i18next from 'i18next';
import type { PlanType } from '@/constants/plans';

// Paywall reason strings are shown at the exact conversion moment to a
// majority-non-EN audience. Translate via the i18n singleton (same pattern as
// src/lib/stripe.ts). Each call carries an English defaultValue so a missing
// key never shows a raw key, and gates stay correct even pre-hydration.
const gt = (key: string, defaultValue: string, opts?: Record<string, unknown>) =>
  i18next.t(key, { defaultValue, ...opts });

export interface FeatureCheckResult {
  allowed: boolean;
  reason?: string;
  daysRemaining?: number;
}

export interface SubscriptionState {
  isPaid: boolean;
  isLite: boolean;
  isStandard?: boolean;
  isPremium?: boolean;
  isPro?: boolean; // "pro or above" — true for Enterprise too (superset of Pro)
  isEnterprise?: boolean;
  isTrial: boolean; // true for free plan users
  isTrialExpired: boolean; // true only for a lapsed card-upfront trial
  trialDaysRemaining: number; // days left in a card-upfront trial, else 0
  /** Inside a paid card-upfront trial: full plan entitlement, temporary. */
  isTrialing?: boolean;
  /** Central export-branding flag. Free users always; trialing only if enabled. */
  shouldWatermark?: boolean;
  isLoading?: boolean;
}

export interface ProjectUsage {
  cameraCount: number;
  fenceCount: number;
  gateCount: number;
  buildingCount: number;
  equipmentCount: number;
  cableRouteCount: number;
}

export const FREE_LIMITS = {
  cameras: 6,
  // fences 1→5, buildings 2→5 (2026-09-02): both walls hit the user before the
  // first camera and barely sold (fence wall 2% clicks after the trial was
  // retired, building wall 13%), while the camera wall is the one that converts.
  // Free stays tight where it sells and loose where it only blocked activation.
  fences: 5,
  gates: 1,
  // buildings + projects raised 1→2 (2026-06): the 1-item caps hard-blocked
  // free users but converted ~4-5% (vs export/portal at 20-36%) — pure friction.
  // Letting a free user sketch 2 buildings / save 2 jobs lifts activation; the
  // paid pull stays on the gates that actually convert.
  buildings: 5,
  projects: 2,
  // equipment 2→6, cableRoutes 1→6 (2026-09-10): matched to the competitor's
  // free tier, which grants 3 infrastructure devices and an uncapped cable
  // planner. Both walls convert (12.4% and 10.9%), so this is a deliberate
  // trade of ~3.7 conversions/month for a free tier that wins the comparison.
  equipment: 6,
  cableRoutes: 6,
  // Tower fleet (2026-08-28): 1 real tower so the whole registry flow is
  // clickable on the user's own data, not a demo (owner: "free musi mieć
  // możliwość zobaczenia jak to wygląda"). Paid caps live in
  // FLEET_TOWER_LIMITS below; this is the free rung of that ladder.
  fleetTowers: 1,
};

// Alias for backwards compatibility
export const TRIAL_LIMITS = FREE_LIMITS;

export const LITE_LIMITS = {
  cameras: 16,
  projects: 5,
};

/**
 * Per-plan limits on the number of floors a single project may contain.
 * Multi-floor is a Premium-and-above feature. Free / Lite / Standard users
 * are limited to a single floor.
 */
/**
 * Physical tower registry (tower_fleet) caps per plan, owner's 2026-08-28
 * ladder. Free stays at 1 so the whole fleet flow is clickable on real data
 * ("free musi mieć możliwość zobaczenia jak to wygląda"); lite mirrors free.
 * Enterprise is uncapped, expressed as the same sentinel the DB uses so the
 * parity gate can compare the numbers verbatim.
 * ⛔ MIRROR of get_fleet_tower_limit_for_user() in the database, asserted by
 * scripts/check-limit-parity.mjs — change both or neither.
 */
export const FLEET_TOWER_LIMITS = {
  free: 1,
  lite: 1,
  standard: 2,
  premium: 10,
  pro: 40,
  enterprise: 2147483647,
};

// Floors per project. Studio and Enterprise have no cap (Infinity: every
// check is `count >= max`, and the pricing table prints "Unlimited").
export const FLOOR_LIMITS = {
  free: 2,
  lite: 2,
  standard: 5,
  premium: 20,
  pro: Infinity,
  enterprise: Infinity,
};

/**
 * Per-plan caps for the NEW (2026-05) Pro/Enterprise tiers — kept here as the
 * single source of truth alongside the other entitlements. These take a plan
 * name (subscription.plan) rather than the boolean bag because seat/company/
 * white-label differentiation needs Pro-vs-Enterprise resolution, which the
 * boolean bag deliberately collapses (isPro = "pro or above"). Callers are the
 * clean account/team management UIs that hold the real plan.
 */

/**
 * Team members allowed, counting the owner, who occupies a `team_members` row.
 * 0 = no team feature on this plan.
 *
 * ⛔ These numbers are a MIRROR of `team_seat_limit_for_owner` in the database,
 * which is what actually enforces the cap on insert. Pro said 5 here while the
 * server enforced 4 (found 2026-08-20 while checking the landing page's "5
 * seats" claim, which came from the same wrong number). Nothing calls this
 * function today, so it was a claim rather than a bug, but a stale entitlement
 * table is how a UI ends up offering a seat the server then refuses.
 *
 * 🔑 A Pro seat is NOT the same thing as an Enterprise seat. On Pro these are
 * view-only invitations: `team_editor_seat_limit_for_owner` returns
 * `LEAST(extra_seats, 10)`, i.e. ZERO until the owner buys editor seats at
 * €10/month each. Enterprise seats can all edit. Any copy derived from this
 * table has to say which kind it is selling.
 */
export function maxTeamSeats(plan: PlanType, includedSeats?: number | null): number {
  // Enterprise Duo and any other bespoke sizing: subscriptions.included_seats
  // replaces the plan's own base (migration 20260912140000). Ordinary
  // Enterprise subscriptions carry NULL and keep the 11 below.
  if (includedSeats != null && plan === 'enterprise') return includedSeats;
  switch (plan) {
    case 'enterprise': return 11;   // owner + 10, all able to edit
    case 'pro': return 4;           // owner + 3, view-only unless seats bought
    case 'premium': return 2;       // owner + 1, able to edit
    default: return 0;
  }
}

/**
 * Team members allowed to EDIT, counting the owner. The gap between this and
 * `maxTeamSeats` is what a Pro customer has to buy, and the reason the pricing
 * copy cannot talk about "seats" without saying which kind.
 */
export function maxEditorSeats(plan: PlanType, purchasedSeats = 0, includedSeats?: number | null): number {
  if (includedSeats != null && plan === 'enterprise') return includedSeats + purchasedSeats;
  switch (plan) {
    case 'enterprise': return 11 + purchasedSeats;
    case 'pro': return Math.min(purchasedSeats, 10);
    case 'premium': return 2;
    default: return 0;
  }
}

/** Company profiles allowed. Standard 1 → Premium 10 → Pro/Enterprise unlimited. */
export function maxCompanies(plan: PlanType): number {
  switch (plan) {
    case 'enterprise':
    case 'pro': return Infinity;
    case 'premium': return 10;
    case 'standard': return 1;
    default: return 1; // free / lite / trial
  }
}

/** White-label client deliverables — remove "made with CCTVplanner" branding
 *  from the PDF and client portal, use the firm's own logo. Pro & Enterprise. */
export function canWhiteLabel(plan: PlanType): boolean {
  return plan === 'pro' || plan === 'enterprise';
}

/** Branded portal URL — own subdomain (Pro) or full custom domain (Enterprise)
 *  for the client portal, instead of cctvplanner.io/portal/<token>. Completes
 *  the white-label story (the URL itself). Infra (DNS/cert/Cloudflare-for-SaaS);
 *  see docs/enterprise-portal-domain-spec.md. Pro & Enterprise. */
export function canUseCustomPortalDomain(plan: PlanType): boolean {
  return plan === 'pro' || plan === 'enterprise';
}

/** SSO/SAML — Enterprise only. Connection management + the SAML auth flow are
 *  infra (Supabase native SAML); see docs/enterprise-sso-spec.md. This is just
 *  the entitlement gate so the UI/edge fn can check it when SSO ships. */
export function canUseSSO(plan: PlanType): boolean {
  return plan === 'enterprise';
}

export function getMaxFloorsForSubscription(subscription: SubscriptionState): number {
  if (subscription.isPro) return FLOOR_LIMITS.pro; // Enterprise too
  if (subscription.isPremium) return FLOOR_LIMITS.premium;
  if (subscription.isStandard) return FLOOR_LIMITS.standard;
  if (subscription.isLite) return FLOOR_LIMITS.lite;
  return FLOOR_LIMITS.free;
}

/** The cheapest plan with more floors per project than the current one. */
export function floorUpgradePlan(subscription: SubscriptionState): 'standard' | 'premium' | 'pro' {
  const max = getMaxFloorsForSubscription(subscription);
  if (FLOOR_LIMITS.standard > max) return 'standard';
  if (FLOOR_LIMITS.premium > max) return 'premium';
  return 'pro';
}

/** floorUpgradePlan for a plan name (the store's plans screen holds the plan, not the flags). */
export function floorUpgradePlanForName(plan: string | null | undefined): 'standard' | 'premium' | 'pro' {
  return floorUpgradePlan({
    isPaid: !!plan && plan !== 'free',
    isLite: plan === 'lite',
    isStandard: plan === 'standard',
    isPremium: plan === 'premium',
    isPro: plan === 'pro' || plan === 'enterprise',
    isTrial: !plan || plan === 'free',
    isTrialExpired: false,
    trialDaysRemaining: 0,
  });
}

// Plan label for analytics (funnel_events.properties.plan). SubscriptionState
// is a bag of booleans, so call sites kept reading `subscription.plan` and
// getting `undefined` — making the /admin/funnel dashboard unable to group
// feature_used by plan. Centralised here so every emitter labels the same way.
export function planNameOf(s: SubscriptionState): 'enterprise' | 'pro' | 'premium' | 'standard' | 'lite' | 'free' {
  if (s.isEnterprise) return 'enterprise';
  if (s.isPro) return 'pro';
  if (s.isPremium) return 'premium';
  if (s.isStandard) return 'standard';
  if (s.isLite) return 'lite';
  return 'free';
}

/**
 * Check whether the current user may switch the multi-floor feature on for
 * their project. Independent of how many floors they already have — this is
 * the gate for the toggle itself. Any plan with room for a second floor.
 */
export function canUseMultiFloor(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (getMaxFloorsForSubscription(subscription) > 1) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_multifloor', 'Multi-floor design requires the Business plan or higher. Upgrade to plan buildings with several floors, each with its own floor plan, cameras, and cables.'),
  };
}

/**
 * Check if user can add another floor to the project.
 */
export function canAddFloor(
  subscription: SubscriptionState,
  currentFloorCount: number,
): FeatureCheckResult {
  if (subscription.isLoading) {
    return currentFloorCount < FLOOR_LIMITS.free
      ? { allowed: true }
      : { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  }
  const max = getMaxFloorsForSubscription(subscription);
  if (currentFloorCount >= max) {
    if (max === 1) {
      return {
        allowed: false,
        reason: gt('gate_floor_needs_premium', 'Multi-floor plans require the Business plan or higher. Upgrade to design buildings with multiple floors in a single project.'),
      };
    }
    return {
      allowed: false,
      reason: gt('gate_floor_cap', 'Floor limit reached ({{count}} floors per project). Upgrade for a higher cap.', { count: max }),
    };
  }
  return { allowed: true };
}

/**
 * There is NO grace period for `past_due`. It means free, from the minute
 * Stripe flips the status. (Decision 2026-08-24.)
 *
 * What was here before: a three-day window, measured from the subscription
 * row's `updated_at`, that kept the plan's entitlements alive. It failed in
 * three separate ways at once, which is why it is gone rather than corrected.
 *
 * It never reached the server. `effective_plan_for()` counts only
 * `active`/`trialing`, so RLS handed a past_due account free limits
 * immediately, while the client still showed paid affordances. Every one of
 * the 15 accounts in past_due on 2026-08-24 resolved to `free` server-side,
 * including those "inside" the window. The grace did not grant access, it only
 * made the UI lie about it, and the save then bounced off RLS.
 *
 * It was inconsistent with itself. `isPaid()` honoured the window while
 * `isPremium()` / `isPro()` / `isEnterprise()` required `active` outright, so a
 * premium customer on day one of past_due kept the generic paid gates and lost
 * every plan-specific one.
 *
 * It never expired for anyone actually using the product. The window was
 * measured from `updated_at`, and the 5-minute `last_seen_at` heartbeat writes
 * that same row, where `update_subscriptions_timestamp` bumps `updated_at`. An
 * active user reset their own grace on every heartbeat.
 */

/**
 * How long a status='trialing' row keeps conferring entitlement after its
 * trial_ends_at. Stripe flips the subscription to active (or past_due) within
 * seconds of the trial ending, but the webhook can lag; without this window a
 * paying customer would briefly lose access at the exact moment they start
 * paying.
 */
export const TRIAL_END_GRACE_HOURS = 24;

/**
 * Whether a trialing subscription is still inside its trial window.
 * A missing or unparseable trial_ends_at returns true: the Stripe-derived
 * status is authoritative, and denying access on a malformed date would lock
 * out a customer whose card is already on file.
 */
export function isTrialWindowActive(trialEndsAt: string | undefined | null): boolean {
  if (!trialEndsAt || trialEndsAt === 'null') return true;
  const endsAt = new Date(trialEndsAt);
  if (isNaN(endsAt.getTime())) return true;
  return Date.now() < endsAt.getTime() + TRIAL_END_GRACE_HOURS * 60 * 60 * 1000;
}

/**
 * Check if user can add a camera to the project
 */
export function canAddCamera(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  // While loading, enforce free plan limits as a safe default.
  // Server-side validation is the final gate; this prevents client-side exploits.
  if (subscription.isLoading) {
    return usage.cameraCount < FREE_LIMITS.cameras
      ? { allowed: true }
      : { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  }

  if (subscription.isLite) {
    if (usage.cameraCount >= LITE_LIMITS.cameras) {
      return {
        allowed: false,
        reason: gt('gate_cameras_lite', 'Lite plan limit reached ({{count}} cameras per project). Upgrade to Installer for unlimited cameras.', { count: LITE_LIMITS.cameras }),
      };
    }
    return { allowed: true };
  }

  if (subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.cameraCount >= FREE_LIMITS.cameras) {
      return {
        allowed: false,
        reason: gt('gate_cameras_free', 'Free plan limit reached ({{count}} cameras per project). Upgrade to add more cameras.', { count: FREE_LIMITS.cameras }),
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: gt('gate_verify', 'Unable to verify subscription. Please refresh the page.') };
}

export interface OverCameraLimitCheck {
  over: boolean;
  cap: number;
  /** Cheapest plan whose cap fits this project; drives the upsell CTA. */
  minimumPlan: 'lite' | 'standard';
}

/**
 * Whether a project's TOTAL camera count (all floors, same measure as the
 * server's camera_limit_for_owner / enforce_camera_plan_limit pair) exceeds
 * what the current plan allows. Powers the designer's view-only mode for
 * projects built under a bigger plan that has since lapsed. Plan order must
 * mirror canAddCamera: isLite before isPaid, free cap while loading, and
 * `over` stays false during loading so a paid user's project never flashes
 * the upsell before the subscription arrives.
 */
export function projectOverCameraLimit(
  subscription: SubscriptionState,
  totalCameraCount: number
): OverCameraLimitCheck {
  const minimumPlan: 'lite' | 'standard' =
    totalCameraCount <= LITE_LIMITS.cameras ? 'lite' : 'standard';
  if (subscription.isLoading) {
    return { over: false, cap: FREE_LIMITS.cameras, minimumPlan };
  }
  const cap = subscription.isLite
    ? LITE_LIMITS.cameras
    : subscription.isPaid || subscription.isTrialing
      ? Number.MAX_SAFE_INTEGER
      : FREE_LIMITS.cameras;
  return { over: totalCameraCount > cap, cap, minimumPlan };
}

/**
 * Check if user can add a fence to the project
 */
export function canAddFence(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.fenceCount < FREE_LIMITS.fences
      ? { allowed: true }
      : { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.fenceCount >= FREE_LIMITS.fences) {
      return {
        allowed: false,
        reason: gt('gate_fences_free', 'Free plan limit reached ({{count}} fences per project). Upgrade to add more fences.', { count: FREE_LIMITS.fences }),
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: gt('gate_verify', 'Unable to verify subscription. Please refresh the page.') };
}

/**
 * Check if user can add a gate to the project
 */
export function canAddGate(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.gateCount < FREE_LIMITS.gates
      ? { allowed: true }
      : { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.gateCount >= FREE_LIMITS.gates) {
      return {
        allowed: false,
        reason: gt('gate_gates_free', 'Free plan limit reached ({{count}} gate per project). Upgrade to add more gates.', { count: FREE_LIMITS.gates }),
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: gt('gate_verify', 'Unable to verify subscription. Please refresh the page.') };
}

/**
 * Check if user can add a building to the project
 */
export function canAddBuilding(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.buildingCount < FREE_LIMITS.buildings
      ? { allowed: true }
      : { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.buildingCount >= FREE_LIMITS.buildings) {
      return {
        allowed: false,
        reason: gt('gate_buildings_free', 'Free plan limit reached ({{count}} buildings per project). Upgrade to add more buildings.', { count: FREE_LIMITS.buildings }),
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: gt('gate_verify', 'Unable to verify subscription. Please refresh the page.') };
}

/**
 * "Building auto-import" — auto-placing every building in a selected area at
 * once. A power feature reserved for paid plans (Lite and above); free users
 * can still place their single building manually. Deliberately data-source
 * agnostic: user-facing copy must NOT name the underlying provider.
 */
export function canBulkImportBuildings(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLite || subscription.isPaid) return { allowed: true };
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  return {
    allowed: false,
    reason: gt('gate_bulk_buildings', 'Building auto-import is available on paid plans. On the free plan you can add up to 5 buildings manually.'),
  };
}

/**
 * "Fence auto-import" — auto-detecting a parcel boundary OR bulk-importing every
 * nearby wall/hedge in one step. Both batch-create fences, so on the free plan
 * they'd bypass the per-project fence cap. Paid-plan only (Lite and above); free
 * users can still draw fences manually. Data-source agnostic copy.
 */
export function canBulkImportFences(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLite || subscription.isPaid) return { allowed: true };
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  return {
    allowed: false,
    reason: gt('gate_bulk_fences', 'Fence auto-detect and bulk wall/hedge import are paid-plan features. On the free plan you can draw fences manually.'),
  };
}

/**
 * Check if user can add equipment to the project
 */
export function canAddEquipment(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.equipmentCount < FREE_LIMITS.equipment
      ? { allowed: true }
      : { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.equipmentCount >= FREE_LIMITS.equipment) {
      return {
        allowed: false,
        reason: gt('gate_equipment_free', 'Free plan limit reached ({{count}} equipment items). Upgrade to add more equipment.', { count: FREE_LIMITS.equipment }),
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: gt('gate_verify', 'Unable to verify subscription. Please refresh the page.') };
}

/**
 * Check if user can add a cable route to the project
 */
export function canAddCableRoute(
  subscription: SubscriptionState,
  usage: ProjectUsage
): FeatureCheckResult {
  if (subscription.isLoading) {
    return usage.cableRouteCount < FREE_LIMITS.cableRoutes
      ? { allowed: true }
      : { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  }

  if (subscription.isLite || subscription.isPaid) {
    return { allowed: true };
  }

  if (subscription.isTrial) {
    if (usage.cableRouteCount >= FREE_LIMITS.cableRoutes) {
      return {
        allowed: false,
        reason: gt('gate_cableroutes_free', 'Free plan limit reached ({{count}} cable routes per project). Upgrade to add more cable routes.', { count: FREE_LIMITS.cableRoutes }),
      };
    }
    return { allowed: true };
  }

  return { allowed: false, reason: gt('gate_verify', 'Unable to verify subscription. Please refresh the page.') };
}

/**
 * Check if user can save a project
 */
export function canSaveProject(
  subscription: SubscriptionState,
  existingProjectCount: number = 0,
  isUpdatingExisting: boolean = false
): FeatureCheckResult {
  // While loading, allow updates but restrict new projects to free limit (1)
  if (subscription.isLoading) {
    if (isUpdatingExisting) return { allowed: true };
    return existingProjectCount < FREE_LIMITS.projects
      ? { allowed: true }
      : { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  }

  if (subscription.isLite) {
    if (isUpdatingExisting) return { allowed: true };
    if (existingProjectCount >= LITE_LIMITS.projects) {
      return {
        allowed: false,
        reason: gt('gate_projects_lite', 'Lite plan limit reached ({{count}} projects max). Upgrade to Installer for unlimited projects.', { count: LITE_LIMITS.projects }),
      };
    }
    return { allowed: true };
  }

  if (subscription.isPaid) {
    return { allowed: true };
  }

  if (isUpdatingExisting) {
    return { allowed: true };
  }

  if (existingProjectCount >= FREE_LIMITS.projects) {
    return {
      allowed: false,
      reason: gt('gate_projects_free', 'Free plan limit reached ({{count}} projects maximum). Upgrade to create more projects.', { count: FREE_LIMITS.projects }),
    };
  }

  return { allowed: true };
}

/**
 * Check if user can export to PDF
 */
export function canExportPDF(subscription: SubscriptionState): FeatureCheckResult {
  return { allowed: true };
}

/**
 * Check if user can access the planner at all
 */
export function canAccessPlanner(subscription: SubscriptionState): FeatureCheckResult {
  return { allowed: true };
}

/**
 * Check if user can use Survey Mode (Standard+ plans)
 */
export function canUseSurveyMode(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPaid && !subscription.isLite) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_survey', 'Survey Mode requires the Installer plan or higher. Upgrade to capture photos, voice notes, and checklists on-site.'),
  };
}

/**
 * Check if user can use Client Portal (Premium+ plans)
 */
export function canUseClientPortal(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_client_portal', 'Client Portal requires the Business plan or higher. Upgrade to share read-only project links with clients.'),
  };
}

/**
 * Check if user can use System Validator (Premium+ plans).
 * (Previously commented as "Standard+" — that contradicted both the code
 * below and the user-facing reason string. Premium is the truth.)
 */
export function canUseSystemValidator(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_validator', 'System Validator requires the Business plan. Upgrade to automatically detect coverage gaps, DORI issues, and cable problems.'),
  };
}

/**
 * Check if user can use BOM (Premium+ plans)
 */
/**
 * Free for everyone since 2026-09-10. The BOM wall converted at 5.8% against
 * the camera wall's 13.1%, and of the ten people it did convert, seven had
 * already hit a capacity wall that would have caught them anyway. The paid
 * pull stays on capacity; see FREE_LIMITS.
 */
export function canUseBOM(subscription: SubscriptionState): FeatureCheckResult {
  return { allowed: true };
}

/**
 * Cable-schedule PDF (per-run cable lengths From → To) — Standard and above
 * (paid, excluding the entry Lite tier). A dedicated one-click export so
 * installers get cable lengths per camera without the Premium CAD/DXF flow.
 */
export function canUseCableSchedule(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPaid && !subscription.isLite) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_cable_schedule', 'The cable schedule PDF requires the Installer plan or higher. Upgrade to export per-camera cable runs and lengths.'),
  };
}

/**
 * Lighting simulation: every PAID tier, Lite included.
 *
 * ── Why the entry tier and not higher ───────────────────────────────────────
 *
 * The feature answers "will this camera see anything at night", which is the
 * question that decides whether a small installer's job succeeds. Putting it
 * above Lite would leave exactly the people who buy the cheapest plan doing
 * that calculation in their heads, and it is the calculation this product
 * exists to do. It is also the clearest reason to leave the free tier, which
 * is where it earns its place in the pricing rather than in the feature list.
 *
 * Covers the illuminance map, the verdict layer, luminaire placement and the
 * lighting page of the document. Gating one of those and not the others would
 * let a design be built and then not read, which is worse than either.
 */
/** Own equipment in the picker (custom_equipment_items): paid plans, the same
 *  rule the table's insert policy enforces. */
export function canUseCustomEquipment(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPaid) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_custom_equipment', 'Adding your own equipment requires a paid plan. Upgrade to add devices missing from the catalog and share them with your team.'),
  };
}

export function canUseLightingSimulation(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPaid) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_lighting', 'Lighting simulation requires a paid plan. Upgrade to place luminaires, compute illuminance and see what each camera actually gets at night.'),
  };
}

/**
 * Check if user can use Wi-Fi coverage (Premium+ plans; owner ruling
 * 2026-08-25). Covers the map layer, the network dialog's Wi-Fi tab and the
 * PDF opt-in: gating one of those and not the others would let a design be
 * built and then not read, the same rule the lighting gate states.
 */
export function canUseWifiCoverage(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_wifi_coverage', 'Wi-Fi coverage requires the Business plan or higher. Upgrade to predict access point signal through the walls you drew.'),
  };
}

/**
 * Check if user can use DXF/CAD export (Premium+ plans).
 * Full-fidelity vector export with layers, blocks, DORI zones, obstacle clipping,
 * multi-floor ZIP packaging and georeferenced floor plan raster embedding.
 */
export function canUseDXFExport(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPremium || subscription.isPro) return { allowed: true };
  return {
    allowed: false,
    reason: gt('gate_dxf', 'DXF export requires the Business plan. Upgrade to export your design to CAD (AutoCAD, BricsCAD, ArchiCAD, Revit, QGIS) with layers, blocks, DORI zones, obstacle clipping and multi-floor packaging.'),
  };
}

/**
 * Check if user can use Smart Quote (Premium+ plans)
 */
/** Free for everyone since 2026-09-10, on the same reasoning as canUseBOM. */
export function canUseSmartQuote(subscription: SubscriptionState): FeatureCheckResult {
  return { allowed: true };
}

export interface Scene3DPreviewAllowance {
  /** Opens of the interactive 3D view allowed inside one window. */
  previews: number;
  /** Rolling window length in hours. */
  windowHours: number;
}

/**
 * How often each plan may open the 3D site view. null = unlimited. Every
 * export from 3D (PDF, PNG, video) stays Pro and above, see canUse3D.
 * ⛔ MIRROR of scene3d_preview_allowance() in the database, asserted by
 * scripts/check-limit-parity.mjs. The paywall copy (scene3d_quota_wall_msg_*)
 * says these numbers in words, so change it with them.
 */
export const SCENE3D_PREVIEW_ALLOWANCE: Record<PlanType, Scene3DPreviewAllowance | null> = {
  free: { previews: 1, windowHours: 720 },
  lite: { previews: 1, windowHours: 720 },
  standard: { previews: 1, windowHours: 168 },
  premium: { previews: 1, windowHours: 24 },
  pro: null,
  enterprise: null,
};

/**
 * Unlimited 3D site view and every export from it: Pro and above (isPro is
 * "pro or above", so Enterprise is included). Lower plans open the view within
 * SCENE3D_PREVIEW_ALLOWANCE instead.
 */
export function canUse3D(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPro) return { allowed: true }; // Pro or above (Pro + Enterprise)
  return {
    allowed: false,
    reason: gt('gate_3d', '3D site view requires the Pro plan or higher. Upgrade to present your design to clients in realistic 3D.'),
  };
}

/**
 * AI camera planner — auto-generates and places cameras for the site.
 * Pro plan or higher (mirrors canUse3D).
 */
export function canUseAiPlanner(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPro) return { allowed: true }; // Pro or above (Pro + Enterprise)
  return {
    allowed: false,
    reason: gt('gate_ai_planner', 'The AI camera planner requires the Pro plan or higher. Upgrade to auto-generate optimal camera placements.'),
  };
}

/**
 * What the assistant may do on the plan in words: Studio and above plan the
 * site (the AI planner), a paid plan makes everyday edits (names, camera
 * settings, drives, the map), Free sees those edits and applies them from
 * Installer.
 */
export function aiAgentTier(subscription: SubscriptionState): 'free' | 'installer' | 'business' | 'studio' {
  if (subscription.isLoading) return 'free';
  if (subscription.isPro) return 'studio';
  if (subscription.isPremium) return 'business';
  return subscription.isPaid ? 'installer' : 'free';
}

/**
 * Line of sight over terrain: the viewshed sweep to tens of kilometres, its
 * map layer, the terrain profile and the smoke-column check. Pro plan or
 * higher (mirrors canUse3D).
 */
export function canUseTerrainViewshed(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPro) return { allowed: true }; // Pro or above (Pro + Enterprise)
  return {
    allowed: false,
    reason: gt('gate_viewshed', 'Line of sight over terrain requires the Pro plan or higher. Upgrade to see which ground a camera really covers out to tens of kilometres, behind which ridge it is hidden, and how high smoke must rise to be seen.'),
  };
}

/**
 * RTSP stream from a camera's 3D view into a recorder or AI box: Pro and above
 * (Studio and Enterprise for customers; isPro is "pro or above"). Owner ruling
 * 2026-10-09.
 */
export function canUsePovRtsp(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPro) return { allowed: true }; // Pro or above (Pro + Enterprise)
  return {
    allowed: false,
    reason: gt('gate_pov_rtsp', 'Streaming a camera view to a recorder over RTSP requires the Studio plan or higher. Upgrade to test recorder and AI analytics on the exact view of a camera before it is installed.'),
  };
}

/** IFC (BIM) export and import: Studio and Enterprise. */
export function canUseIFC(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPro) return { allowed: true }; // Pro or above (Pro + Enterprise)
  return {
    allowed: false,
    reason: gt('gate_ifc', 'IFC export and import (Revit, ArchiCAD and other BIM tools) require the Studio plan or higher. Upgrade to exchange your design with architects as objects with their parameters.'),
  };
}

/** CSV camera import: per-project camera ceiling for Pro. Enterprise has none.
 *  Counts TOTAL cameras in the project after import, not per-file. */
export const CAMERA_IMPORT_LIMIT_PRO = 400;

/**
 * CSV camera import (map an existing installation) — Pro (≤400 cameras per
 * project) or Enterprise (unlimited). The limit is enforced again at apply
 * time; this gate only answers "may this plan use the importer at all".
 */
export function canImportCameras(
  subscription: SubscriptionState
): FeatureCheckResult & { cameraLimit?: number } {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isEnterprise) return { allowed: true, cameraLimit: Infinity };
  if (subscription.isPro) return { allowed: true, cameraLimit: CAMERA_IMPORT_LIMIT_PRO };
  return {
    allowed: false,
    reason: gt('gate_camera_import', 'CSV camera import requires the Studio plan (up to {{limit}} cameras) or Enterprise (unlimited). Upgrade to map your existing installation.', { limit: CAMERA_IMPORT_LIMIT_PRO }),
  };
}

/**
 * CSV camera export (hand the camera list to installers / asset systems) —
 * Pro or Enterprise, mirroring the CSV importer's gate. No count limit:
 * exporting is always allowed on an eligible plan, even for grandfathered
 * over-limit projects — it's the user's own data.
 */
export function canExportCamerasCsv(subscription: SubscriptionState): FeatureCheckResult {
  if (subscription.isLoading) return { allowed: false, reason: gt('gate_loading', 'Loading subscription...') };
  if (subscription.isPro) return { allowed: true }; // Pro or above (Pro + Enterprise)
  return {
    allowed: false,
    reason: gt('gate_camera_csv_export', 'CSV camera export requires the Studio plan or higher. Upgrade to hand your camera list to installers and asset systems.'),
  };
}

/**
 * Get a summary of free plan limits and current usage for a project
 */
export function getTrialLimitsSummary(usage: ProjectUsage): string {
  return (
    `Cameras: ${usage.cameraCount}/${FREE_LIMITS.cameras} • ` +
    `Fences: ${usage.fenceCount}/${FREE_LIMITS.fences} • ` +
    `Gates: ${usage.gateCount}/${FREE_LIMITS.gates} • ` +
    `Buildings: ${usage.buildingCount}/${FREE_LIMITS.buildings}`
  );
}
